import React, { useEffect, useState } from 'react';
import Image from 'components/image/Image';
import {
  availableAbout,
  availableRelocate,
  manageExperience,
  monthlySalary,
  workExperience
} from 'core/constants/preferenceConstats';
import { contractTypes } from 'core/constants/workType';
import {
  calculateMatchPercentage,
  extractCompetencyInfo,
  getComparedData,
  getMatchingScores,
  consolidateCategories
} from 'helpers/teaserView';
import Button from 'components/Button/Index';
import useLocales from 'hooks/useLocales';
import CompareChart from '../CompareChart';
import { getDateFormatter } from 'helpers/dateConverter';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

import './Index.scss';

const ProfilePopUp = ({
  onClick,
  selectedRequest,
  getJob,
  id,
  status,
  isCandidate = true
}: any): JSX.Element => {
  const [candidateInfo, setCandidateInfo] = useState<any>({
    contract: '',
    workExp: '',
    managementExp: '',
    availability: '',
    willingness: '',
    salary: '',
    name: '',
    avatar: '',
    pitch: '',
    industries: [],
    studies: [],
    skills: [],
    birthdate: '',
    languages: [],
    competencies: [],
    score: 0,
    culture_score: 0
  });
  const [talentMatchPercent, setTalentMatchPercent] = useState<
    number | null | string
  >();
  const [cultureMatchPercent, setCultureMatchPercent] = useState<
    number | null | string
  >();
  const [competencyInfo, setCompetencyInfo] = useState<any>({
    names: [],
    descriptions: [],
    minScores: []
  });
  const [cultureInfo, setCultureInfo] = useState<any>({
    cultureName: [],
    cultureDescriptions: [],
    cultureMinScores: []
  });
  const { t } = useLocales();
  const assessment = useSelector((state: RootState) => state.assessment);

  useEffect(() => {
    if (!selectedRequest?.candidate) return;

    // Check if the candidate is an object with industries property
    // This is very confusing, but it's how the data is structured

    const baseCandidate = selectedRequest?.candidate?.industries
      ? selectedRequest?.candidate
      : getJob?.selected_candidates?.[0]?.candidate;

    const contract = contractTypes
      .filter((e) => baseCandidate.contract?.includes(+e.value))
      .map((e) => e.to_show)
      .join(', ');
    const workExp = workExperience.find(
      (el: any) => +el.value === baseCandidate.work_exp
    )?.to_show;
    const managementExp = manageExperience.find(
      (el: any) => +el.value === baseCandidate.management_exp
    )?.to_show;
    const availability = availableAbout.find(
      (el: any) => +el.value === baseCandidate.availability
    )?.to_show;
    const willingness = availableRelocate.find(
      (item) => +item.value === baseCandidate.relocation
    )?.to_show;
    const salary = monthlySalary.find(
      (item) => +item.value === baseCandidate.salary
    )?.to_show;

    setCandidateInfo({
      contract,
      workExp,
      managementExp,
      availability,
      willingness,
      salary,
      name: baseCandidate.name,
      avatar: baseCandidate.avatar,
      pitch: baseCandidate.pitch,
      industries: baseCandidate.industries || [],
      studies: baseCandidate.studies || [],
      skills: baseCandidate.skills || [],
      birthdate: baseCandidate.birthdate,
      languages: baseCandidate.languages || [],
      competencies: baseCandidate.competencies || [],
      score: baseCandidate.score || selectedRequest?.score || 0,
      culture_score:
        baseCandidate.culture_score || selectedRequest?.culture_score || 0
    });

    // Extract job competencies info
    const { names, descriptions, minScores } = extractCompetencyInfo(
      getJob?.competencies || []
    );
    const {
      names: cultureName,
      descriptions: cultureDescriptions,
      minScores: cultureMinScores
    } = extractCompetencyInfo(getJob?.culture_profile?.competencies || []);

    setCompetencyInfo({ names, descriptions, minScores });
    setCultureInfo({ cultureName, cultureDescriptions, cultureMinScores });

    // Talent match
    const talentScores = getMatchingScores(
      getJob?.competencies || [],
      candidateInfo.competencies
    );
    const percentages =
      calculateMatchPercentage(talentScores, minScores) ||
      candidateInfo.score ||
      0;

    // Culture match
    const cultureScores = getMatchingScores(
      getJob?.culture_profile?.competencies || [],
      candidateInfo.competencies
    );

    const percentagesCulture =
      calculateMatchPercentage(cultureScores, cultureMinScores) ||
      candidateInfo.culture_score ||
      0;

    setTalentMatchPercent(percentages);
    setCultureMatchPercent(percentagesCulture);
  }, [selectedRequest, getJob]);

  // Update match percentages when candidateInfo changes
  useEffect(() => {
    if (!candidateInfo.competencies.length) return;

    // Talent match
    const talentScores = getMatchingScores(
      getJob?.competencies || [],
      candidateInfo.competencies
    );
    const percentages =
      calculateMatchPercentage(talentScores, competencyInfo.minScores) ||
      candidateInfo.score ||
      0;

    // Culture match
    const cultureScores = getMatchingScores(
      getJob?.culture_profile?.competencies || [],
      candidateInfo.competencies
    );

    const percentagesCulture =
      calculateMatchPercentage(cultureScores, cultureInfo.cultureMinScores) ||
      candidateInfo.culture_score ||
      0;

    setTalentMatchPercent(percentages);
    setCultureMatchPercent(percentagesCulture);
  }, [candidateInfo.competencies, competencyInfo, cultureInfo, getJob]);

  return (
    <div className="teaser">
      <div className="teaser__top">
        <div className="teaser__top__profile teaser-block">
          <Image
            className="teaser__top__profile-avatar"
            url={candidateInfo.avatar}
            name={!candidateInfo.avatar ? 'no-avatar.svg' : ''}
            width={115}
            height={115}
            objectFit="cover"
          />
          <span className="teaser__top__profile-role about-item">
            {candidateInfo.name}
          </span>
          {talentMatchPercent !== undefined && talentMatchPercent !== null && (
            <span className="teaser__top__profile-talent about-item">
              {isNaN(Number(talentMatchPercent)) ? 0 : talentMatchPercent}%
              Talent Match
            </span>
          )}
          {cultureMatchPercent !== undefined &&
            cultureMatchPercent !== null && (
              <span className="teaser__top__profile-talent about-item sec">
                {isNaN(Number(cultureMatchPercent)) ? 0 : cultureMatchPercent}%
                Cultural Fit
              </span>
            )}
          {candidateInfo.pitch && (
            <p className="teaser__top__profile-about about-item">
              {`"${candidateInfo.pitch}"`}
            </p>
          )}
        </div>
        <div className="teaser__top__second">
          <div className="teaser__top__second__cards">
            <Image name="industry.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Industries preferences
              </span>
              {candidateInfo.industries.map((item: any) => (
                <span
                  key={item.id}
                  className="teaser__top__second__cards__cont-title"
                >
                  {item.name}
                </span>
              ))}
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="study.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">Study</span>
              {candidateInfo.studies.map((item: any) => (
                <span
                  key={item.id}
                  className="teaser__top__second__cards__cont-title"
                >
                  {item.name}
                </span>
              ))}
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="preferred.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Preferred contract
              </span>
              <span className="teaser__top__second__cards__cont-title">
                {candidateInfo.contract}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="compSkill.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Computer Skills
              </span>
              {candidateInfo.skills.map((item: any) => (
                <span
                  key={item.id}
                  className="teaser__top__second__cards__cont-title"
                >
                  {item.name}
                </span>
              ))}
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="managment.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Management experience
              </span>
              <span className="teaser__top__second__cards__cont-title">
                {candidateInfo.managementExp}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="careerLevel.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Career Level
              </span>
              <span className="teaser__top__second__cards__cont-title">
                {candidateInfo.workExp}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="availability.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Availability
              </span>
              <span className="teaser__top__second__cards__cont-title">
                {candidateInfo.availability}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="willing.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Willing to relocate
              </span>
              <span className="teaser__top__second__cards__cont-title">
                {candidateInfo.willingness}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="study.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Birthdate
              </span>
              <span className="teaser__top__second__cards__cont-title">
                {getDateFormatter(candidateInfo.birthdate, 'MMM. DD, YYYY')}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="salaries.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">Salary</span>
              <span className="teaser__top__second__cards__cont-title">
                {candidateInfo.salary}
              </span>
            </div>
          </div>
          <div className="teaser__top__second__cards">
            <Image name="languages.svg" width={30} height={30} />
            <div className="teaser__top__second__cards__description">
              <span className="teaser__top__second__cards__names">
                Languages
              </span>
              {candidateInfo.languages.map((item: any) => (
                <span
                  key={item.id}
                  className="teaser__top__second__cards__cont-title"
                >
                  {item.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="teaser__bottom">
        <CompareChart
          percent={talentMatchPercent}
          names={competencyInfo?.names}
          className="jobdetails__chart-left bg_styles"
          minScores={competencyInfo?.minScores}
          descriptions={competencyInfo?.descriptions}
          comparePoints={
            getComparedData(candidateInfo.competencies, getJob?.competencies) ||
            []
          }
          title={'Match job competencies'}
          showDescription={true}
        />
        <CompareChart
          percent={cultureMatchPercent}
          className="jobdetails__chart-right bg_styles"
          names={cultureInfo?.cultureName}
          minScores={cultureInfo?.cultureMinScores}
          descriptions={cultureInfo?.cultureDescriptions}
          comparePoints={
            getComparedData(
              candidateInfo.competencies,
              getJob?.culture_profile?.competencies
            ) || []
          }
          title={'Match culture competencies'}
          showDescription={true}
        />
      </div>
      {id && status !== 4 && status !== 2 && (
        <div className="btn_wrapper">
          <Button
            className="cancel_request"
            title={isCandidate ? 'Decline Request' : 'Withdraw Request'}
            t={t}
            onClick={onClick}
          />
        </div>
      )}
    </div>
  );
};

export default ProfilePopUp;
